import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover'
import { Typography } from '@/shared/components/ui/typography'
import { formatRelativeTime } from '@/shared/utils/timeFormatting'
import type { CollaboratorWithLastViewed } from '@/types/collaborator'
import React from 'react'
import UserAvatar from '../../shared/components/UserAvatar'

type CollaboratorAvatarsProps = {
  collaborators: CollaboratorWithLastViewed[]
  maxVisible?: number
}

export const CollaboratorAvatars: React.FC<CollaboratorAvatarsProps> = ({
  collaborators,
  maxVisible = 4,
}) => {
  if (!Array.isArray(collaborators) || collaborators.length === 0) {
    return null
  }

  const visibleCollaborators = collaborators.slice(0, maxVisible)
  const hiddenCount = collaborators.length - maxVisible

  return (
    <Popover>
      <PopoverTrigger asChild>
        <div className="flex cursor-pointer items-center">
          <div className="flex items-center -space-x-2">
            {visibleCollaborators.map((collaborator, index) => (
              <div key={collaborator._id || index} className="relative">
                <UserAvatar
                  email={collaborator?.email ?? ''}
                  initials={`${collaborator.firstName?.[0] || ''}`}
                  className="h-8 w-8 border-2 border-white"
                />
              </div>
            ))}
          </div>
          {hiddenCount > 0 && (
            <div className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full text-xs font-medium text-gray-600">
              +{hiddenCount}
            </div>
          )}
        </div>
      </PopoverTrigger>
      <PopoverContent
        className="-light/50 w-64 rounded-sm bg-grey-04 p-0"
        align="start"
        side="bottom"
      >
        {/* Top caret - positioned on the left */}
        <div className="absolute -top-1 left-4 h-0 w-0 transform border-b-4 border-l-4 border-r-4 border-transparent border-b-mydra-black"></div>

        {/* Title */}
        <div className="px-4 pt-3">
          <Typography className="text-[9px] font-medium text-gray-400">
            LAST VIEWED
          </Typography>
        </div>

        {/* Collaborators list */}
        <div className="py-2">
          {collaborators.map((collaborator, index) => (
            <div
              key={collaborator._id || index}
              className="flex items-center gap-3 px-4 py-1 transition-colors hover:bg-gray-700"
            >
              <UserAvatar
                email={collaborator.email}
                initials={`${collaborator.firstName?.[0] || ''}`}
                className="h-5 w-5 flex-shrink-0"
              />
              <div className="min-w-0 flex-1">
                <Typography
                  variant="caption"
                  className="truncate font-semibold text-white"
                >
                  {collaborator.firstName} {collaborator.lastName}
                </Typography>
              </div>
              <div className="flex-shrink-0 text-[9px] text-gray-400">
                {collaborator.lastViewed
                  ? formatRelativeTime(collaborator.lastViewed)
                  : 'Never'}
              </div>
            </div>
          ))}
        </div>

        {/* Bottom caret - positioned on the left */}
        <div className="absolute -bottom-1 left-4 h-0 w-0 transform border-l-4 border-r-4 border-t-4 border-transparent border-t-green-500"></div>
      </PopoverContent>
    </Popover>
  )
}
